import { createSlice } from '@reduxjs/toolkit';

// Load settings from localStorage
const loadSettings = () => {
  const defaultSettings = {
    appTheme: 'teal',
    appDarkMode: true,
    appGradientEnabled: true,
    appHeaderFixed: true,
    appHeaderInverse: true,
    appSidebarFixed: true,
    appSidebarGrid: false,
  };

  try {
    const storedSettings = localStorage.getItem('appSettings');
    if (storedSettings) {
      const parsedSettings = JSON.parse(storedSettings);

      // Merge with defaults and enforce baseline app settings on load.
      // Do not force appDarkMode here — it must persist from parsedSettings/localStorage.
      const mergedSettings = {
        ...defaultSettings,
        ...parsedSettings,
        appTheme: 'teal',
        appSidebarGrid: false,
        // Sticky sidebar: keep fixed while main content scrolls (requires fixed header).
        appSidebarFixed: true,
        appHeaderFixed: true,
      };

      localStorage.setItem('appSettings', JSON.stringify(mergedSettings));
      return mergedSettings;
    }
  } catch (error) {
    console.error('Error loading app settings from localStorage:', error);
  }
  
  // Return default settings
  return defaultSettings;
};

// Save settings to localStorage
const saveSettings = (settings) => {
  try {
    localStorage.setItem('appSettings', JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving app settings to localStorage:', error);
  }
};

const initialState = loadSettings();

const appSettingsSlice = createSlice({
  name: 'appSettings',
  initialState,
  reducers: {
    setAppTheme: (state, action) => {
      state.appTheme = action.payload;
      saveSettings(state);
    },
    setAppDarkMode: (state, action) => {
      state.appDarkMode = action.payload;
      saveSettings(state);
    },
    setAppGradientEnabled: (state, action) => {
      state.appGradientEnabled = action.payload;
      saveSettings(state);
    },
    setAppHeaderFixed: (state, action) => {
      state.appHeaderFixed = action.payload;
      saveSettings(state);
    },
    setAppHeaderInverse: (state, action) => {
      state.appHeaderInverse = action.payload;
      saveSettings(state);
    },
    setAppSidebarFixed: (state, action) => {
      state.appSidebarFixed = action.payload;
      saveSettings(state);
    },
    setAppSidebarGrid: (state, action) => {
      state.appSidebarGrid = action.payload;
      saveSettings(state);
    },
    resetAppSettings: (state) => {
      const defaultSettings = {
        appTheme: 'teal',
        appDarkMode: true,
        appGradientEnabled: true,
        appHeaderFixed: true,
        appHeaderInverse: true,
        appSidebarFixed: true,
        appSidebarGrid: false,
      };
      Object.assign(state, defaultSettings);
      saveSettings(state);
    },
  },
});

export const {
  setAppTheme,
  setAppDarkMode,
  setAppGradientEnabled,
  setAppHeaderFixed,
  setAppHeaderInverse,
  setAppSidebarFixed,
  setAppSidebarGrid,
  resetAppSettings,
} = appSettingsSlice.actions;

export default appSettingsSlice.reducer;
